import React, { useMemo, useState } from "react";
import { useStore, serializeProject, parseProjectJson } from "../state/store";
import { namespaceImpact, validateNamespace } from "../model/validation";
import { slugifyNamespace } from "../model/defaults";
import { FullIdPreview, IssueList, Modal } from "../ui/common";
import { downloadText, readFileAsText } from "../ui/files";

export function SettingsPanel() {
  const project = useStore((s) => s.project);
  const setNamespace = useStore((s) => s.setNamespace);
  const setGuideItemId = useStore((s) => s.setGuideItemId);
  const updateMeta = useStore((s) => s.updateMeta);
  const replaceProject = useStore((s) => s.replaceProject);
  const resetProject = useStore((s) => s.resetProject);

  const [draftNs, setDraftNs] = useState(project.namespace);
  const [pendingNs, setPendingNs] = useState<string | null>(null);

  // Keep the draft in sync if the project is replaced (import/reset).
  React.useEffect(() => setDraftNs(project.namespace), [project.namespace]);

  const nsIssues = useMemo(() => validateNamespace(draftNs), [draftNs]);
  const nsValid = !nsIssues.some((i) => i.severity === "error");
  const impact = useMemo(() => namespaceImpact(project), [project]);

  const commitNamespace = () => {
    if (!nsValid || draftNs === project.namespace) return;
    setPendingNs(draftNs);
  };

  const confirmNamespace = () => {
    if (pendingNs) setNamespace(pendingNs);
    setPendingNs(null);
  };

  const onImportProject = async (file: File) => {
    try {
      const text = await readFileAsText(file);
      replaceProject(parseProjectJson(text));
    } catch (e) {
      alert(`Could not load project: ${(e as Error).message}`);
    }
  };

  return (
    <div className="panel">
      <h2>Project settings</h2>

      <section className="card">
        <h3>Namespace</h3>
        <p className="hint">
          The single source of truth for every identifier. Changing this <strong>renames every item, recipe, and
          translation key</strong>, which are stored as bare name-parts and composed on export.
        </p>
        <div className="ns-row">
          <input
            className={nsValid ? "" : "invalid"}
            value={draftNs}
            spellCheck={false}
            onChange={(e) => setDraftNs(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commitNamespace()}
          />
          <button
            className="btn"
            onClick={() => setDraftNs(slugifyNamespace(project.meta.name))}
            title="Suggest from pack name"
          >
            Suggest
          </button>
          <button className="btn primary" disabled={!nsValid || draftNs === project.namespace} onClick={commitNamespace}>
            Apply
          </button>
        </div>
        <div className="preview-line">
          Example item id: <FullIdPreview namespace={draftNs} id={project.guideItemId} />
        </div>
        <IssueList issues={nsIssues} />
      </section>

      <section className="card">
        <h3>Guide item</h3>
        <label className="field">
          <span className="field-label">Guide item name-part</span>
          <input
            value={project.guideItemId}
            spellCheck={false}
            onChange={(e) => setGuideItemId(e.target.value.trim())}
          />
        </label>
        <div className="preview-line">
          Full id: <FullIdPreview namespace={project.namespace} id={project.guideItemId} />
        </div>
      </section>

      <section className="card">
        <h3>Pack details</h3>
        <label className="field">
          <span className="field-label">Name</span>
          <input value={project.meta.name} onChange={(e) => updateMeta({ name: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Description</span>
          <input value={project.meta.description} onChange={(e) => updateMeta({ description: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Author</span>
          <input value={project.meta.author} onChange={(e) => updateMeta({ author: e.target.value })} />
        </label>
      </section>

      <section className="card">
        <h3>Project file</h3>
        <p className="hint">Save your work as a JSON project you can reload later. This is not the exported pack.</p>
        <div className="btn-row">
          <button
            className="btn"
            onClick={() => downloadText(serializeProject(project), `${project.namespace}.guidebook.json`)}
          >
            Save project (.json)
          </button>
          <label className="btn">
            Load project…
            <input
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => e.target.files?.[0] && onImportProject(e.target.files[0])}
            />
          </label>
          <button
            className="btn danger"
            onClick={() => confirm("Start a brand-new empty project? Unsaved changes are lost.") && resetProject()}
          >
            New empty project
          </button>
        </div>
      </section>

      {pendingNs && (
        <Modal
          title="Rename namespace?"
          onClose={() => setPendingNs(null)}
          actions={
            <>
              <button className="btn" onClick={() => setPendingNs(null)}>
                Cancel
              </button>
              <button className="btn primary" onClick={confirmNamespace}>
                Rename to “{pendingNs}”
              </button>
            </>
          }
        >
          <p>
            Renaming <code>{project.namespace}</code> → <code>{pendingNs}</code> will re-derive, in one atomic pass:
          </p>
          <ul className="impact-list">
            <li>
              <strong>{impact.items}</strong> item identifier{impact.items === 1 ? "" : "s"} (incl. the guide item)
            </li>
            <li>
              <strong>{impact.recipes}</strong> recipe reference{impact.recipes === 1 ? "" : "s"}
            </li>
            <li>
              <strong>{impact.langKeys}</strong> lang key{impact.langKeys === 1 ? "" : "s"}
            </li>
            <li>
              <strong>{impact.textureKeys}</strong> texture short-name{impact.textureKeys === 1 ? "" : "s"}
            </li>
          </ul>
          <p className="hint">Manifest UUIDs are not affected.</p>
        </Modal>
      )}
    </div>
  );
}
